import type { ViewController } from '@presentation/shell/NexxusShell';
import { CabinetPortfolioService } from '@application/licence/CabinetPortfolioService';
import { TenantLifecycleService } from '@application/licence/TenantLifecycleService';
import { CabinetAccessService } from '@application/licence/CabinetAccessService';
import { SecureLicenceStore } from '@infra/licence/SecureLicenceStore';
import { ActivationService } from '@infra/licence/ActivationService';
import { assertActivationAllowed } from '@application/licence/ActivationInstallGuard';
import { TenantProvisioningService } from '@application/licence/TenantProvisioningService';
import { ToastService } from '@presentation/components/Toast';
import { esc as escapeHtml } from '@presentation/shell/format';
import { renderPortfolioHybridBackupHtml } from '@presentation/shell/PortfolioHybridBackupStatus';
import { renderPortfolioLicenceBadgeHtml } from '@presentation/shell/PortfolioLicenceBadge';

function fmtNum(n: number): string {
  return new Intl.NumberFormat('fr-FR').format(Math.round(n));
}

export const cabinetController: ViewController = async (section, ctx, shell) => {
  const multi = await CabinetPortfolioService.isMultiDossierMode(ctx);
  const cabinet = CabinetPortfolioService.isCabinetMode(ctx);
  if (!multi && !cabinet) {
    const list = await CabinetPortfolioService.listSummaries(ctx);
    section.innerHTML = `
      <div class="page-header"><div><h1>Tableau de bord dossiers</h1>
      <div class="subtitle">Gérez plusieurs entreprises sur ce poste</div></div></div>
      <div class="empty" style="padding:40px;text-align:center;max-width:560px;margin:0 auto;">
        <p>Pour traiter plusieurs sociétés, ouvrez le <strong>Portefeuille clients</strong> et activez
        une clé commerciale Nexxus par entreprise (mode local).</p>
        <p style="font-size:12px;color:var(--gris);">Dossiers déjà activés sur ce poste : ${list.length}.</p>
        <button type="button" class="btn btn-primary" data-go-portefeuille>Ouvrir le portefeuille</button>
      </div>`;
    section.querySelector('[data-go-portefeuille]')?.addEventListener('click', () => {
      void shell.navigate('portefeuille');
    });
    return;
  }

  const list = await CabinetPortfolioService.listSummaries(ctx);
  const cnt = section.querySelector('#cabinet-count');
  if (cnt) {
    cnt.textContent = `${list.length} entreprise${list.length > 1 ? 's' : ''} au portefeuille`;
  }

  const el = section.querySelector('#cabinet-content');
  if (!el) {
    // Vue remplacée entièrement ci-dessus ou markup manquant
    return;
  }

  if (!list.length) {
    el.innerHTML =
      '<div class="empty" style="padding:40px;text-align:center;">Aucune entreprise dans le portefeuille. Activez une clé commerciale cliente ou ouvrez le portefeuille.</div>';
    return;
  }

  let effTot = 0;
  for (const t of list) effTot += t.effectif ?? 0;

  const rows = (
    await Promise.all(
      list.map(async (t) => {
        const licenceBadge = await renderPortfolioLicenceBadgeHtml(t.id, t.licensed);
        return `<tr class="${t.active ? 'cab-total' : ''}">
      <td><strong>${escapeHtml(t.nom)}</strong><br>
        <span style="font-size:11px;color:var(--gris);">${escapeHtml(t.activite || '')}${t.ville ? ' · ' + escapeHtml(t.ville) : ''}</span></td>
      <td class="num">${t.effectif ?? 0}</td>
      <td class="num">${licenceBadge}</td>
      <td class="num" style="white-space:nowrap;">
        <button class="btn btn-sm btn-primary" data-open-client="${escapeHtml(t.id)}">Ouvrir</button>
      </td>
    </tr>`;
      }),
    )
  ).join('');

  el.innerHTML = `
    <div class="cab-kpis">
      <div class="cab-kpi"><div class="cab-kpi-val">${list.length}</div><div class="cab-kpi-lab">Entreprises clientes</div></div>
      <div class="cab-kpi"><div class="cab-kpi-val">${fmtNum(effTot)}</div><div class="cab-kpi-lab">Effectif total (approximatif)</div></div>
    </div>
    <div style="margin-top:16px;background:#fff;border:1px solid #E3E9ED;border-radius:12px;padding:16px;">
      <h3 style="margin:0 0 10px;">Détail par entreprise</h3>
      <div style="overflow-x:auto;"><table class="cab-table"><thead><tr>
        <th>Entreprise</th><th class="num">Effectif</th><th class="num">Licence</th><th></th>
      </tr></thead><tbody>${rows}</tbody></table></div>
    </div>`;

  el.querySelectorAll<HTMLElement>('[data-open-client]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-open-client');
      if (id) void openClient(ctx, shell, id);
    });
  });
};

export const portefeuilleController: ViewController = async (section, ctx, shell) => {
  const list = await CabinetPortfolioService.listSummaries(ctx, { includeArchived: true });
  const el = section.querySelector('#portefeuille-content');
  if (!el) {
    section.innerHTML = `
      <div class="page-header"><div><h1>Portefeuille clients</h1>
      <div class="subtitle">Multi-dossiers — une clé commerciale par entreprise</div></div></div>
      <div id="portefeuille-content"></div>`;
  }
  const content = section.querySelector('#portefeuille-content');
  if (!content) return;

  const activateBlock = `
    <div class="card" style="margin-bottom:16px;padding:16px;">
      <h3 style="margin:0 0 8px;">Activer une entreprise / un dossier</h3>
      <p style="font-size:12px;color:var(--gris);margin:0 0 10px;">
        Chaque entreprise = une clé commerciale Nexxus. Collez la clé fournie, puis <strong>Ouvrir</strong>.
        En haut s’affiche l’entreprise ouverte ; dès qu’il y en a 2, la liste permet de passer de l’une à l’autre.
      </p>
      <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;">
        <textarea id="cabinet-activate-key" class="input" rows="2" style="flex:1;min-width:220px;resize:vertical;"
          placeholder="NXS-XXXX-XXXX-XXXX-XXXX" autocomplete="off" spellcheck="false"></textarea>
        <button type="button" class="btn btn-primary" id="cabinet-activate-btn">Activer et ajouter</button>
      </div>
      <p id="cabinet-activate-msg" style="font-size:12px;margin:8px 0 0;color:var(--gris);"></p>
    </div>`;

  if (!list.length) {
    content.innerHTML =
      activateBlock +
      '<div class="empty" style="padding:40px;text-align:center;">Aucune entreprise cliente activée sur ce poste.</div>';
  } else {
    content.innerHTML =
      activateBlock +
      '<div class="portfolio-grid">' +
      (
        await Promise.all(
          list.map(async (t) => {
            const logo = escapeHtml((t.nom || '?').substring(0, 3).toUpperCase());
            const licenceBadge = await renderPortfolioLicenceBadgeHtml(t.id, t.licensed);
            return `<div class="portfolio-card${t.active ? ' is-active' : ''}">
          <div class="pf-head">
            <div class="pf-logo">${logo}</div>
            <div class="pf-title">
              <div class="pf-name">${escapeHtml(t.nom)}</div>
              <div class="pf-sub">${escapeHtml(t.activite || '—')}${t.ville ? ' · ' + escapeHtml(t.ville) : ''}</div>
            </div>
            ${t.active ? '<span class="pf-badge-active">Active</span>' : ''}
          </div>
          <div class="pf-kpis">
            <div><span class="pf-k">${t.effectif ?? 0}</span><span class="pf-l">Effectif</span></div>
            <div><span class="pf-k">${licenceBadge}</span><span class="pf-l">Licence</span></div>
            ${renderPortfolioHybridBackupHtml(t.id, { isActive: Boolean(t.active) })}
          </div>
          <div class="pf-foot">
            <span class="${t.archived ? 'pf-alert' : t.active ? 'pf-ok' : 'pf-alert'}">${
              t.archived ? 'Archivé' : t.active ? 'Dossier ouvert' : 'Basculer'
            }</span>
            <div style="display:flex;gap:6px;flex-wrap:wrap;">
              ${
                t.archived
                  ? `<button class="btn btn-sm btn-secondary" data-restore-client="${escapeHtml(t.id)}">Réactiver</button>`
                  : `<button class="btn btn-sm btn-primary" data-open-client="${escapeHtml(t.id)}" ${t.active ? 'disabled' : ''}>Ouvrir</button>
                     <button class="btn btn-sm btn-secondary" data-archive-client="${escapeHtml(t.id)}" ${t.active ? 'disabled title="Ouvrez un autre dossier avant d’archiver"' : ''}>Archiver</button>`
              }
            </div>
          </div>
        </div>`;
          }),
        )
      ).join('') +
      '</div>';
  }

  content.querySelectorAll<HTMLElement>('[data-open-client]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-open-client');
      if (id) void openClient(ctx, shell, id);
    });
  });

  content.querySelectorAll<HTMLElement>('[data-archive-client]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-archive-client');
      if (id) void archiveClient(ctx, shell, id);
    });
  });

  content.querySelectorAll<HTMLElement>('[data-restore-client]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-restore-client');
      if (id) void restoreClient(ctx, shell, id);
    });
  });

  const actBtn = content.querySelector('#cabinet-activate-btn');
  actBtn?.addEventListener('click', () => void activateClientKey(ctx, shell, section));
};

async function openClient(
  ctx: Parameters<ViewController>[1],
  shell: Parameters<ViewController>[2],
  tenantId: string,
): Promise<void> {
  const result = await CabinetPortfolioService.switchToTenant(ctx, tenantId);
  if (!result.ok) {
    ToastService.error(result.error);
    return;
  }
  ToastService.success('Entreprise ouverte');
  await shell.refreshAfterTenantSwitch();
  await shell.navigate('dashboard');
}


async function archiveClient(
  ctx: Parameters<ViewController>[1],
  shell: Parameters<ViewController>[2],
  tenantId: string,
): Promise<void> {
  const ok = window.confirm(
    'Archiver ce dossier ? Les salariés et bulletins sont conservés. Le dossier ne pourra plus être ouvert tant qu’il n’est pas réactivé.',
  );
  if (!ok) return;
  const result = await TenantLifecycleService.archiveTenant(ctx, tenantId);
  if (!result.ok) {
    ToastService.error(result.error);
    return;
  }
  ToastService.success('Dossier archivé — données conservées');
  await shell.navigate('portefeuille', { replace: true });
}

async function restoreClient(
  ctx: Parameters<ViewController>[1],
  shell: Parameters<ViewController>[2],
  tenantId: string,
): Promise<void> {
  const result = await TenantLifecycleService.restoreTenant(ctx, tenantId);
  if (!result.ok) {
    ToastService.error(result.error);
    return;
  }
  ToastService.success('Dossier réactivé');
  await shell.navigate('portefeuille', { replace: true });
}

async function activateClientKey(
  ctx: Parameters<ViewController>[1],
  shell: Parameters<ViewController>[2],
  section: HTMLElement,
): Promise<void> {
  const input = section.querySelector<HTMLTextAreaElement | HTMLInputElement>(
    '#cabinet-activate-key',
  );
  const msg = section.querySelector('#cabinet-activate-msg');
  const key = (input?.value || '').replace(/\s+/g, '').trim();
  if (!key) {
    if (msg) msg.textContent = 'Saisissez une clé commerciale.';
    return;
  }
  if (msg) msg.textContent = 'Activation en cours…';
  const { setInstallDataConsent } = await import('@infra/licence/installConsentStore');
  const { INSTALL_DATA_POLICY_VERSION } = await import('@domain/legal/InstallDataPolicy');
  if (!setInstallDataConsent(INSTALL_DATA_POLICY_VERSION)) {
    if (msg) msg.textContent = 'Impossible d’enregistrer le consentement à la politique de données.';
    ToastService.error('Impossible d’enregistrer le consentement à la politique de données.');
    return;
  }
  const sessionBefore = ctx.auth.getSession();
  const homeTenant = await CabinetPortfolioService.captureActiveTenant();
  const activation = new ActivationService();
  // SEC C4 — portefeuille : uniquement une clé entreprise (pas une clé Cabinet).
  const result = await activation.activateClient(key, (claims) =>
    assertActivationAllowed(ctx, claims),
  );
  if (!result.ok) {
    if (msg) msg.textContent = result.error || 'Activation refusée.';
    ToastService.error(result.error || 'Activation refusée.');
    return;
  }
  const runtime = ctx.getLicenceRuntime();
  if (runtime) await runtime.loadFromStore();
  await TenantProvisioningService.ensureTenantFromLicence(ctx);
  const addedTenant = await SecureLicenceStore.getActiveTenantId();
  // Le cabinet reste sur son dossier : l'entreprise ajoutée s'ouvre depuis le sélecteur, sur demande.
  await CabinetPortfolioService.restoreActiveTenant(ctx, homeTenant);
  // Octroi explicite (P0-6) : le cabinet qui détient la clé peut ouvrir l'entreprise qu'il vient d'ajouter
  // (sa fiche utilisateur n'est lisible que depuis son dossier d'origine, d'où le retour préalable).
  await CabinetAccessService.grantAfterClientActivation(ctx, sessionBefore, addedTenant);
  if (msg) msg.textContent = 'Entreprise ajoutée au portefeuille.';
  ToastService.success('Licence activée et dossier ajouté');
  await shell.refreshAfterTenantSwitch();
  await shell.navigate('portefeuille', { replace: true });
}
